// useAudioScrubber — minimal play/pause/scrub transport for a single audio
// source. The `src` can be a local blob: URL (a just-recorded preview) OR a
// remote Storage download URL (a sent echo being replayed) — it's pure native
// <audio>, so it doesn't care which.
//
// Shared by both apps (學員 Echo preview + 學員/工作員 echo replay) via
// @goodbye/ui. Unlike the learner app's useAudioPlayback (which owns resume,
// media-session, prev/next for the audio playlist), this stays tiny: one clip,
// no persistence.
//
// Pure native <audio> — no Web Audio, no <audio controls> (that native element
// breaks the grayscale design system; a custom grayscale player is the right
// call). Caller renders its own scrub bar.

import { useCallback, useEffect, useRef, useState } from "react";

export interface UseAudioScrubberResult {
  audioRef: React.RefObject<HTMLAudioElement>;
  audioProps: {
    preload: "metadata";
    onLoadedMetadata: () => void;
    onPlay: () => void;
    onPause: () => void;
    onTimeUpdate: () => void;
    onEnded: () => void;
  };
  playing: boolean;
  elapsed: number;
  total: number;
  play: () => Promise<void>;
  pause: () => void;
  togglePlay: () => Promise<void>;
  seekToFraction: (f: number) => void;
}

export function useAudioScrubber(
  src: string,
  fallbackDuration: number,
): UseAudioScrubberResult {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [total, setTotal] = useState(0);

  // Reset transport when the source changes.
  useEffect(() => {
    setPlaying(false);
    setElapsed(0);
    setTotal(0);
  }, [src]);

  const readDuration = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    const d = el.duration;
    if (Number.isFinite(d) && d > 0) setTotal(d);
    else if (fallbackDuration > 0) setTotal(fallbackDuration);
  }, [fallbackDuration]);

  const play = useCallback(async () => {
    const el = audioRef.current;
    if (!el) return;
    try {
      await el.play();
      setPlaying(true);
    } catch {
      setPlaying(false);
    }
  }, []);

  const pause = useCallback(() => {
    audioRef.current?.pause();
    setPlaying(false);
  }, []);

  const togglePlay = useCallback(async () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) await play();
    else pause();
  }, [pause, play]);

  const seekToFraction = useCallback(
    (f: number) => {
      const el = audioRef.current;
      const dur = total > 0 ? total : fallbackDuration;
      if (!el || dur <= 0) return;
      el.currentTime = Math.max(0, Math.min(1, f)) * dur;
      setElapsed(el.currentTime);
    },
    [total, fallbackDuration],
  );

  return {
    audioRef,
    audioProps: {
      preload: "metadata",
      onLoadedMetadata: readDuration,
      onPlay: () => setPlaying(true),
      onPause: () => setPlaying(false),
      onTimeUpdate: () => {
        const el = audioRef.current;
        if (el) setElapsed(el.currentTime);
      },
      onEnded: () => {
        setPlaying(false);
        setElapsed(0);
      },
    },
    playing,
    elapsed,
    total: total > 0 ? total : fallbackDuration,
    play,
    pause,
    togglePlay,
    seekToFraction,
  };
}
